import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import './CashFlowWidget.css';
import useResizeNotifier from '../../hooks/useResizeNotifier';
import useContainerWidth from '../../hooks/useContainerWidth';
import {
  BaseWidgetProps,
  CommonTooltipType,
  WidgetHeightMessage,
} from '../../types/common.type';
import {
  formatNumber,
  formatKMBT,
  getCssVariable,
  getDecimalPlaces,
} from '../../utils/utils';
import { Region, ThemeType, WidgetNameSource } from '../../utils/type';
import Loading from '../../Components/Loading/Loading';
import ErrorMessage from '../../Components/ErrorMessage/ErrorMessage';
import { errorMsg } from '../../constants/common.constant';
import Layout from '../../Components/Common/Layout/Layout';
import moment from 'moment';
import { getCashFlow } from '../../api/company.api';
import { ReactComponent as ArrowDownIcon } from '../../assets/icons/arrow-down.svg';
import ReactECharts from 'echarts-for-react';
import { FiscalYear, WidgetMessageType } from '../../utils/type';
import CommonTooltip from '../../Components/Common/CommonTooltip/CommonTooltip';
export interface CashFlowWidgetProps extends BaseWidgetProps {
  token?: string;
  symbol?: string;
  select?: FiscalYear;
  onHeightChange?: (message: WidgetHeightMessage) => void;
}

interface CashFlowData {
  data_index: number;
  ric: string;
  period_end_date: string;
  fiscal_year: number;
  cashFrOperations: number;
  cashFrInvesting: number;
  cashFrFinancing: number;
  freeCashFlow: number;
  netCashBeginningBal: number;
  netCashEndingBal: number;
  curr_fin: string | null;
  // pre-computed growth fields (percentage, e.g. 13.5 means +13.5%)
  netCashBeginningBalGrowth?: number | null;
  cashFrOperationsGrowth?: number | null;
  cashFrInvestingGrowth?: number | null;
  cashFrFinancingGrowth?: number | null;
  netCashEndingBalGrowth?: number | null;
}

interface ExpandedRows {
  [key: string]: boolean;
}

const CashFlowWidget = (props: CashFlowWidgetProps) => {
  const { onHeightChange } = props;

  const theme = props.theme ?? ThemeType.Direct;
  const [isEmptyData, setIsEmptyData] = useState(false);
  const msgItem = errorMsg.find((obj) => obj.key === 'cashFlow');
  const widgetRef = useRef<HTMLDivElement | null>(null);
  useResizeNotifier(widgetRef, WidgetNameSource.CashFlow, onHeightChange);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const { isSmallScreen } = useContainerWidth(widgetRef);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const onLoadingChangeRef = useRef(props.onLoadingChange);
  onLoadingChangeRef.current = props.onLoadingChange;
  useEffect(() => {
    onLoadingChangeRef.current?.(isLoading);
  }, [isLoading]);
  const [currFin, setCurrFin] = useState<string | null>(null);
  const [data, setData] = useState<CashFlowData[]>([]);
  const [expandedRows, setExpandedRows] = useState<ExpandedRows>({});
  const [tooltip, setTooltip] = useState<CommonTooltipType>({
    content: '',
    show: false,
    x: 0,
    y: 0,
  });
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  // Filter data for small screen - show only selected fiscal year
  const displayData = useMemo(() => {
    if (!isSmallScreen || data.length === 0) {
      return data.length > 5 ? data.slice(-5) : data;
    }
    const dataIndexMap: { [key: string]: number } = {
      [FiscalYear.Latest]: 1,
      [FiscalYear.Prior1Year]: 2,
      [FiscalYear.Prior2Years]: 3,
      [FiscalYear.Prior3Years]: 4,
      [FiscalYear.Prior4Years]: 5,
      [FiscalYear.Prior5Years]: 6,
    };
    const targetIndex = dataIndexMap[props.select ?? FiscalYear.Latest];
    const found = data.find((item) => item.data_index === targetIndex);
    return [found ?? data[0]];
  }, [data, isSmallScreen, props.select]);

  // Hide tooltip on scroll
  useEffect(() => {
    const handleScroll = () => {
      setTooltip((prev) => ({ ...prev, show: false }));
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  // Call API when props changes

  // Format value to raw value
  const formatMillion = (value: number | null | undefined): number | null => {
    if (value === null || value === undefined) return null;
    return value * 1000000;
  };
  // Calculate growth percentage , handle negative values to Absolute value
  const calculateGrowth = (
    current: number,
    previous: number | null,
  ): number | null => {
    if (previous === null || previous === 0) return null;
    return ((current - previous) / Math.abs(previous)) * 100;
  };

  // Format growth percentage
  const formatGrowth = (
    growth: number | null,
  ): { value: string; isPositive: boolean } => {
    if (growth === null) return { value: '-', isPositive: true };
    const formatted = formatNumber(
      growth.toString(),
      2,
      2,
      false,
      true,
      false,
      '%',
      '-',
    );
    return { value: formatted, isPositive: growth >= 0 };
  };

  // Stacked bar chart config for Cash flow metrics
  const barChartOption = useMemo(() => {
    if (!data || data.length === 0) return {};

    // x-axis labels (fiscal years)
    const years = data.map((item) => item.fiscal_year.toString());

    const operationsSeries = data.map((item) =>
      formatMillion(item.cashFrOperations as number | null),
    );
    const investingSeries = data.map((item) =>
      formatMillion(item.cashFrInvesting as number | null),
    );
    const financingSeries = data.map((item) =>
      formatMillion(item.cashFrFinancing as number | null),
    );
    return {
      tooltip: {
        appendTo: 'body',
        confine: true,
        margin: 20,
        outside: false,
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any[]) => {
          if (!params || !params.length) return '';
          const yearLabel = params[0].axisValue;
          const lines = params
            .filter((p) => p.value !== null && p.value !== undefined)
            .map((p) => {
              const valStr = `${formatKMBT(p.value.toString(), 1, 1, false).value}${formatKMBT(p.value.toString(), 1, 1, false).suffix}`;
              return `${p.marker} ${p.seriesName}: ${valStr}`;
            });
          return [yearLabel, ...lines].join('<br/>');
        },
      },
      legend: {
        bottom: 0,
        left: 'center',
        data: ['Operations', 'Investing', 'Financing'],
        textStyle: {
          fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
          fontSize: 10,
          color: '#3C3C4380',
        },
        itemWidth: 12,
        itemHeight: 8,
      },
      grid: {
        left: 8,
        right: 36,
        top: 20,
        bottom: 50,
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: years,
        axisLine: {
          lineStyle: { color: '#E0E0E0' },
        },
        axisTick: { show: false },
        axisLabel: {
          fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
          fontSize: 10,
          color: '#3C3C4380',
        },
      },
      yAxis: {
        type: 'value',
        position: 'right',
        axisLine: {
          lineStyle: { color: '#E0E0E0' },
        },
        splitLine: {
          lineStyle: { color: '#F0F0F0' },
        },
        axisLabel: {
          interval: 0,
          fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
          fontSize: 10,
          color: '#3C3C4380',
          formatter: (val: number) => {
            if (val === 0) return '0';
            return `${formatKMBT(val.toString(), 1, 1, false).value}${formatKMBT(val.toString(), 1, 1, false).suffix}`;
          },
        },
      },
      series: [
        {
          name: 'Operations',
          type: 'bar',
          minBarWidth: 10,
          maxBarWidth: 30,
          itemStyle: {
            color: getCssVariable('--chart-primary', theme),
          },
          label: {
            show: !isSmallScreen,
            position: 'top',
            formatter: (p: any) =>
              p.value !== null && p.value !== undefined
                ? `${formatKMBT(p.value.toString(), 1, 1, false).value}${formatKMBT(p.value.toString(), 1, 1, false).suffix}`
                : '',
            fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
            fontSize: 10,
            color: '#3C3C4380',
          },
          data: operationsSeries,
        },
        {
          name: 'Investing',
          type: 'bar',
          minBarWidth: 10,
          maxBarWidth: 30,
          itemStyle: {
            color: getCssVariable('--chart-secondary', theme),
          },
          label: {
            show: !isSmallScreen,
            position: 'bottom',
            formatter: (p: any) =>
              p.value !== null && p.value !== undefined
                ? `${formatKMBT(p.value.toString(), 1, 1, false).value}${formatKMBT(p.value.toString(), 1, 1, false).suffix}`
                : '',
            fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
            fontSize: 10,
            color: '#3C3C4380',
          },
          data: investingSeries,
        },
        {
          name: 'Financing',
          type: 'bar',
          minBarWidth: 10,
          maxBarWidth: 30,
          itemStyle: {
            color: getCssVariable('--chart-tertiary', theme),
          },
          label: {
            show: !isSmallScreen,
            position: 'top',
            formatter: (p: any) =>
              p.value !== null && p.value !== undefined
                ? `${formatKMBT(p.value.toString(), 1, 1, false).value}${formatKMBT(p.value.toString(), 1, 1, false).suffix}`
                : '',
            fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
            fontSize: 10,
            color: '#3C3C4380',
          },
          data: financingSeries,
        },
      ],
    };
  }, [data, isSmallScreen, theme]);

  const fetchCashFlowData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res: any = await getCashFlow(props.symbol || '', props.token || '');
      if (res) {
        //find curr_fin from res.data.
        const curr_fin = res.data.find(
          (item: any) => item.curr_fin !== null,
        )?.curr_fin;
        setCurrFin(curr_fin);
        // 1) Sort all years by fiscal year (ascending)
        const sortedData: CashFlowData[] = [...res.data].sort(
          (a: CashFlowData, b: CashFlowData) => a.fiscal_year - b.fiscal_year,
        );

        // 2) Pre-compute growth using full history (before limiting to 5 years)
        const enrichedData: CashFlowData[] = sortedData.map(
          (item, index, arr) => {
            const prev = index > 0 ? arr[index - 1] : null;

            const netCashBeginningBalGrowth = prev
              ? calculateGrowth(
                  item.netCashBeginningBal,
                  prev.netCashBeginningBal,
                )
              : null;
            const cashFrOperationsGrowth = prev
              ? calculateGrowth(item.cashFrOperations, prev.cashFrOperations)
              : null;
            const cashFrInvestingGrowth = prev
              ? calculateGrowth(item.cashFrInvesting, prev.cashFrInvesting)
              : null;
            const cashFrFinancingGrowth = prev
              ? calculateGrowth(item.cashFrFinancing, prev.cashFrFinancing)
              : null;
            const netCashEndingBalGrowth = prev
              ? calculateGrowth(item.netCashEndingBal, prev.netCashEndingBal)
              : null;

            return {
              ...item,
              netCashBeginningBalGrowth,
              cashFrOperationsGrowth,
              cashFrInvestingGrowth,
              cashFrFinancingGrowth,
              netCashEndingBalGrowth,
            };
          },
        );

        setData(enrichedData);
        setIsEmptyData(enrichedData.length === 0);
        if (enrichedData.length === 0) {
          props.onNoData?.({
            type: WidgetMessageType.NoData,
            source: 'CashFlowWidget',
            data: null,
          });
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch cash flow data');
      console.error('Error fetching cash flow data:', err);
      props.onError?.({
        message: err.message,
        type: err.name + '_' + err.code,
      });
      setIsEmptyData(true);
    } finally {
      setIsLoading(false);
    }
  }, [props.token, props.symbol]);

  useEffect(() => {
    fetchCashFlowData();
  }, [props.token, props.symbol, fetchCashFlowData]);

  // Tooltip positioning
  const constrainTooltipPosition = (e: React.MouseEvent) => {
    let x = e.pageX + 15;
    let y = e.pageY;

    const elementMaxWidth = tooltipRef.current?.clientWidth || 0;
    const elementMaxHeight = tooltipRef.current?.clientHeight || 0;
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;
    const viewportWidth = window.innerWidth + scrollX;
    const viewportHeight = window.innerHeight + scrollY;

    if (x + elementMaxWidth > viewportWidth) {
      x = x - elementMaxWidth;
    }
    if (y + elementMaxHeight > viewportHeight) {
      y = y - elementMaxHeight;
    }

    if (x < 0) {
      x = 10;
    }

    return { x, y };
  };

  // Toggle row expansion
  const toggleRow = (rowKey: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowKey]: !prev[rowKey],
    }));
  };

  return (
    <Layout theme={theme}>
      <div ref={widgetRef} className="cf-container">
        {isLoading && !props.onLoadingChange ? (
          <Loading />
        ) : !isLoading && isEmptyData ? (
          <ErrorMessage msg={msgItem} />
        ) : (
          <div className="cf-content-wrapper">
            <div className="cf-chart-container">
              {data && data.length > 0 && (
                <ReactECharts
                  option={barChartOption}
                  style={{
                    width: '100%',
                    height: 180,
                    marginTop: 20,
                    paddingTop: 20,
                  }}
                />
              )}
            </div>
            <div ref={tableContainerRef} className="cf-table-container">
              <table className="cf-table">
                <thead>
                  <tr>
                    <th></th>
                    <th className="cf-table-header-label">
                      <div className="cf-currency-label">
                        Unit million ({currFin ?? ''})
                      </div>
                    </th>
                    {displayData.map((item) => (
                      <th
                        key={item.fiscal_year}
                        className="cf-table-header-year"
                      >
                        <div className="cf-header-content">
                          <div className="cf-header-fy">
                            FY{item.fiscal_year}
                          </div>
                          <div className="cf-header-date">
                            {moment(item.period_end_date).format('DD MMM YYYY')}
                          </div>
                        </div>
                      </th>
                    ))}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {/* Net cash - beginning balance */}
                  <tr className="cf-table-row">
                    <td></td>
                    <td className="cf-table-label-cell">
                      <span
                        className="cf-label-text"
                        onMouseEnter={(e) => {
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip({
                            show: true,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                            content: 'Net cash - beginning balance',
                          });
                        }}
                        onMouseMove={(e) => {
                          if (e.movementX === 0 && e.movementY === 0) {
                            return;
                          }
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip((prev) => ({
                            ...prev,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                          }));
                        }}
                        onMouseLeave={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                        onMouseOut={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                      >
                        Net cash - beginning balance
                      </span>
                    </td>
                    {displayData.map((item) => {
                      const dp = getDecimalPlaces(item.curr_fin);
                      const displayValue =
                        item.netCashBeginningBal !== null &&
                        item.netCashBeginningBal !== undefined
                          ? formatNumber(
                              item.netCashBeginningBal.toString(),
                              dp,
                              dp,
                            )
                          : '-';
                      const growthFormatted =
                        item.netCashBeginningBalGrowth !== undefined &&
                        item.netCashBeginningBalGrowth !== null
                          ? formatGrowth(item.netCashBeginningBalGrowth)
                          : { value: '-', isPositive: true };
                      return (
                        <td key={item.fiscal_year} className="cf-table-cell">
                          <div className="cf-cell-content">
                            <span className="cf-cell-value">
                              {displayValue}
                            </span>
                            {growthFormatted && (
                              <span
                                className={`cf-cell-growth ${growthFormatted.isPositive ? 'positive' : 'negative'}`}
                              >
                                {growthFormatted.value}
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                    <td></td>
                  </tr>

                  {/* Cash from operations */}
                  <tr className="cf-table-row">
                    <td></td>
                    <td className="cf-table-label-cell">
                      <span
                        className="cf-label-text"
                        onMouseEnter={(e) => {
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip({
                            show: true,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                            content: 'Cash from operations',
                          });
                        }}
                        onMouseMove={(e) => {
                          if (e.movementX === 0 && e.movementY === 0) {
                            return;
                          }
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip((prev) => ({
                            ...prev,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                          }));
                        }}
                        onMouseLeave={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                        onMouseOut={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                      >
                        Cash from operations
                      </span>
                    </td>
                    {displayData.map((item) => {
                      const dp = getDecimalPlaces(item.curr_fin);
                      const displayValue =
                        item.cashFrOperations !== null &&
                        item.cashFrOperations !== undefined
                          ? formatNumber(
                              item.cashFrOperations.toString(),
                              dp,
                              dp,
                            )
                          : '-';
                      const growthFormatted =
                        item.cashFrOperationsGrowth !== undefined &&
                        item.cashFrOperationsGrowth !== null
                          ? formatGrowth(item.cashFrOperationsGrowth)
                          : { value: '-', isPositive: true };
                      return (
                        <td key={item.fiscal_year} className="cf-table-cell">
                          <div className="cf-cell-content">
                            <span className="cf-cell-value">
                              {displayValue}
                            </span>
                            {growthFormatted && (
                              <span
                                className={`cf-cell-growth ${growthFormatted.isPositive ? 'positive' : 'negative'}`}
                              >
                                {growthFormatted.value}
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                    <td></td>
                  </tr>

                  {/* Cash from investing */}
                  <tr className="cf-table-row">
                    <td></td>
                    <td className="cf-table-label-cell">
                      <span
                        className="cf-label-text"
                        onMouseEnter={(e) => {
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip({
                            show: true,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                            content: 'Cash from investing',
                          });
                        }}
                        onMouseMove={(e) => {
                          if (e.movementX === 0 && e.movementY === 0) {
                            return;
                          }
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip((prev) => ({
                            ...prev,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                          }));
                        }}
                        onMouseLeave={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                        onMouseOut={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                      >
                        Cash from investing
                      </span>
                    </td>
                    {displayData.map((item) => {
                      const dp = getDecimalPlaces(item.curr_fin);
                      const displayValue =
                        item.cashFrInvesting !== null &&
                        item.cashFrInvesting !== undefined
                          ? formatNumber(
                              item.cashFrInvesting.toString(),
                              dp,
                              dp,
                            )
                          : '-';
                      const growthFormatted =
                        item.cashFrInvestingGrowth !== undefined &&
                        item.cashFrInvestingGrowth !== null
                          ? formatGrowth(item.cashFrInvestingGrowth)
                          : { value: '-', isPositive: true };
                      return (
                        <td key={item.fiscal_year} className="cf-table-cell">
                          <div className="cf-cell-content">
                            <span className="cf-cell-value">
                              {displayValue}
                            </span>
                            {growthFormatted && (
                              <span
                                className={`cf-cell-growth ${growthFormatted.isPositive ? 'positive' : 'negative'}`}
                              >
                                {growthFormatted.value}
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                    <td></td>
                  </tr>

                  {/* Cash from financing */}
                  <tr className="cf-table-row">
                    <td></td>
                    <td className="cf-table-label-cell">
                      <span
                        className="cf-label-text"
                        onMouseEnter={(e) => {
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip({
                            show: true,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                            content: 'Cash from financing',
                          });
                        }}
                        onMouseMove={(e) => {
                          if (e.movementX === 0 && e.movementY === 0) {
                            return;
                          }
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip((prev) => ({
                            ...prev,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                          }));
                        }}
                        onMouseLeave={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                        onMouseOut={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                      >
                        Cash from financing
                      </span>
                    </td>
                    {displayData.map((item) => {
                      const dp = getDecimalPlaces(item.curr_fin);
                      const displayValue =
                        item.cashFrFinancing !== null &&
                        item.cashFrFinancing !== undefined
                          ? formatNumber(
                              item.cashFrFinancing.toString(),
                              dp,
                              dp,
                            )
                          : '-';
                      const growthFormatted =
                        item.cashFrFinancingGrowth !== undefined &&
                        item.cashFrFinancingGrowth !== null
                          ? formatGrowth(item.cashFrFinancingGrowth)
                          : { value: '-', isPositive: true };
                      return (
                        <td key={item.fiscal_year} className="cf-table-cell">
                          <div className="cf-cell-content">
                            <span className="cf-cell-value">
                              {displayValue}
                            </span>
                            {growthFormatted && (
                              <span
                                className={`cf-cell-growth ${growthFormatted.isPositive ? 'positive' : 'negative'}`}
                              >
                                {growthFormatted.value}
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                    <td></td>
                  </tr>

                  {/* Net cash - ending balance */}
                  <tr className="cf-table-row">
                    <td></td>
                    <td className="cf-table-label-cell">
                      <span
                        className="cf-label-text"
                        onMouseEnter={(e) => {
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip({
                            show: true,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                            content: 'Net cash - ending balance',
                          });
                        }}
                        onMouseMove={(e) => {
                          if (e.movementX === 0 && e.movementY === 0) {
                            return;
                          }
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip((prev) => ({
                            ...prev,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                          }));
                        }}
                        onMouseLeave={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                        onMouseOut={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                      >
                        Net cash - ending balance
                      </span>
                    </td>
                    {displayData.map((item) => {
                      const dp = getDecimalPlaces(item.curr_fin);
                      const displayValue =
                        item.netCashEndingBal !== null &&
                        item.netCashEndingBal !== undefined
                          ? formatNumber(
                              item.netCashEndingBal.toString(),
                              dp,
                              dp,
                            )
                          : '-';
                      const growthFormatted =
                        item.netCashEndingBalGrowth !== undefined &&
                        item.netCashEndingBalGrowth !== null
                          ? formatGrowth(item.netCashEndingBalGrowth)
                          : { value: '-', isPositive: true };
                      return (
                        <td key={item.fiscal_year} className="cf-table-cell">
                          <div className="cf-cell-content">
                            <span className="cf-cell-value">
                              {displayValue}
                            </span>
                            {growthFormatted && (
                              <span
                                className={`cf-cell-growth ${growthFormatted.isPositive ? 'positive' : 'negative'}`}
                              >
                                {growthFormatted.value}
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      <CommonTooltip
        ref={tooltipRef}
        content={tooltip.content}
        show={tooltip.show}
        x={tooltip.x}
        y={tooltip.y}
      />
    </Layout>
  );
};

export default CashFlowWidget;
